import React, { useState, useRef } from 'react';
import { withStyles, withTheme } from '@glu/theming';
import { useTrapFocus } from '@glu/utilities-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import useModalFocus from 'hooks/useModalFocus';
import useKeyHandler from 'hooks/useKeyHandler';
import { JSSClasses } from '../../proptypes/jss-prop-types';
import styles from './Modal.styles';

import Fade from '../transitions/Fade';

export const ModalComponent = ({
  children,
  classes,
  closeButton,
  footer,
  header,
  headerStyles,
  mountOnEnter,
  onHide,
  show,
  theme: {
    transitions: {
      duration: { short }
    }
  },
  transition: Transition,
  transition,
  unmountOnExit,
  variant
}) => {
  const [transitionIn, setTransitionIn] = useState(true);
  const modalRef = useRef();

  const transitionDone = (event) => {
    setTransitionIn(true);
    onHide(event);
  };

  const closeModal = (event) => {
    if (typeof transition === 'string') {
      onHide(event);
      return;
    }

    setTransitionIn(false);
    setTimeout(() => transitionDone(event), short);
  };

  const { backdrop, content, dialog, header: modalHeader, modal, modalIn } = classes;

  const showTransition = show && transitionIn;
  const styles = classes[variant];

  /** @description Remove the props when a '<div>' is supplied. */
  let transitionProps = {
    in: show && transitionIn,
    mountOnEnter,
    unmountOnExit
  };

  /** @description Remove the props when a '<div>' is supplied. */
  if (typeof Transition === 'string') {
    transitionProps = {};
  }

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRef, active: show });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal });

  const onKeyDown = useTrapFocus({ ref: modalRef, active: transitionIn });

  return (
    <section className="ModalComponent">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <section
        ref={modalRef}
        onKeyDown={onKeyDown}
        tabIndex="-1"
        className={classnames('modal', modal, { [modalIn]: show })}
        role="dialog"
        aria-label={getAriaLabel()}
      >
        <Fade in={showTransition} mountOnEnter={mountOnEnter} unmountOnExit={unmountOnExit}>
          <div className={backdrop} onClick={closeModal} />
        </Fade>
        <Transition {...transitionProps}>
          <div className={classnames('dialog', dialog)} role="document">
            <section className={content}>
              {header && (
                <article className={`${modalHeader} ${styles} ${headerStyles || ''}`}>
                  {closeButton && (
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                      onClick={closeModal}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  )}
                  {header}
                </article>
              )}
              <article className="modal-body">{children}</article>
              {footer && <article className="modal-footer">{footer}</article>}
            </section>
          </div>
        </Transition>
      </section>
    </section>
  );
};

ModalComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  classes: JSSClasses,
  closeButton: PropTypes.bool,
  // Too many prop type errors. Takes string or react component
  // eslint-disable-next-line react/forbid-prop-types
  transition: PropTypes.any,
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  headerStyles: PropTypes.string,
  mountOnEnter: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  theme: PropTypes.shape({}),
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    'success',
    'warning',
    'info',
    'error'
  ])
};

ModalComponent.defaultProps = {
  children: [],
  classes: {},
  closeButton: true,
  transition: Fade,
  footer: null,
  header: null,
  headerStyles: null,
  mountOnEnter: true,
  onHide() {},
  show: false,
  theme: {},
  unmountOnExit: true,
  variant: 'default'
};

const ModalStyled = withStyles(styles)(ModalComponent);

export default withTheme(ModalStyled);
